<script setup lang="ts">
import { computed, ref } from 'vue';

import { getImageUrl } from '@apps/main/modules/api/hotels';
import {
  useBrowserStore,
  useCoreStore,
} from '@apps/main/new-desktop/core/stores';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import type { ContentApiImage } from '@libs/trip-planner-interfaces';
import type { GalleryButton } from '@libs/ui';
import {
  AppFullscreenMasonryGallery,
  AppMasonryGallery,
  AppFullscreenSingleImageGallery,
  GalleryButtonType,
} from '@libs/ui';

import { GalleryType } from '../models';

interface ActivityDetailsGalleryProps {
  galleryType: GalleryType;
  productText: string;
  images: ContentApiImage[];
  activityCode: string;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  amountImagesToDisplay?: 1 | 2 | 3 | 4 | 5;
  orientation?: 'vertical' | 'horizontal';
  shouldHideButtonText?: boolean;
}

const props = defineProps<ActivityDetailsGalleryProps>();

const { t } = i18n.global;

const isFullscreenGalleryShown = ref<boolean>(false);
const isFullScreenSingleImageGalleryShown = ref<boolean>(false);
const currentSlideIndex = ref<number>(0);

const browserStore = useBrowserStore();
const coreStore = useCoreStore();

function toggleFullscreenGallery(): void {
  isFullscreenGalleryShown.value = !isFullscreenGalleryShown.value;
}

function openFullScreenSingleImageGallery(index: number): void {
  currentSlideIndex.value = index;
  isFullScreenSingleImageGalleryShown.value = true;
}

// FYI: this is temporary solution, we should use change it to proper implementation
function getImageUrlWithQuality(
  image: ContentApiImage,
  quality: 'small' | 'normal' | 'good' | 'large'
): string {
  const smallSize = 600;
  const normalSize = 800;
  const goodSize = 1200;
  const largeSize = 1600;

  if (quality === 'small') {
    return getImageUrl(image, props.activityCode, smallSize, smallSize);
  }

  if (quality === 'good') {
    return getImageUrl(image, props.activityCode, goodSize, goodSize);
  }

  if (quality === 'large') {
    return getImageUrl(image, props.activityCode, largeSize, largeSize);
  }

  return getImageUrl(image, props.activityCode, normalSize, normalSize);
}

function getImagesForMasonryGallery(): string[] {
  const defaultGallerySize = 5;
  const gallerySize = props.amountImagesToDisplay ?? defaultGallerySize;
  const firstImage = props.images[0];
  if (firstImage !== undefined && props.images.length > 0) {
    return [
      getImageUrlWithQuality(firstImage, 'normal'),
      ...props.images
        .slice(1, gallerySize)
        .map((image) => getImageUrlWithQuality(image, 'small')),
    ];
  }
  return [];
}

function getImagesForFullScreenGallery(): string[] {
  const quality = browserStore.isSmallerThan1200px ? 'small' : 'normal';
  return props.images.map((image) => getImageUrlWithQuality(image, quality));
}

function getImagesForSingleImageGallery(): string[] {
  const quality = browserStore.isSmallerThan1200px ? 'small' : 'large';
  return props.images.map((image) => getImageUrlWithQuality(image, quality));
}

function closeFullScreenSingleImageGallery(): void {
  isFullScreenSingleImageGalleryShown.value = false;
}

const galleryButton = computed<GalleryButton>(() => {
  if (props.shouldHideButtonText) {
    return {
      type: GalleryButtonType.IconButton,
      ariaLabel: t('a11y.open_image_gallery'),
    };
  } else {
    return {
      type: GalleryButtonType.TextAndIconButton,
      text: t('planner.show_all_x_images', [props.images.length]),
    };
  }
});
</script>

<template>
  <div class="activity-details-gallery">
    <AppMasonryGallery
      v-if="props.galleryType === GalleryType.Masonry && props.images.length"
      :image-urls="getImagesForMasonryGallery()"
      :alt-image-text="$t('a11y.image')"
      :orientation="props.orientation ?? 'horizontal'"
      :gallery-button="galleryButton"
      class="masonry-gallery"
      @show-full-gallery="toggleFullscreenGallery"
    />
    <AppFullscreenMasonryGallery
      v-if="isFullscreenGalleryShown"
      :image-urls="getImagesForFullScreenGallery()"
      :images-text="$t('planner.images')"
      :product-text="props.productText"
      :close-gallery-aria-label="$t('a11y.close_image_gallery')"
      :error-image-handler="
        (src) =>
          coreStore.handleImageLoadError({
            src: src,
            source: 'activity-details-fullscreen-mansory-gallery',
          })
      "
      @close="toggleFullscreenGallery"
      @image-clicked="openFullScreenSingleImageGallery($event)"
    />
    <AppFullscreenSingleImageGallery
      v-if="isFullScreenSingleImageGalleryShown"
      :image-urls="getImagesForSingleImageGallery()"
      :initial-slide="currentSlideIndex"
      :close-gallery-aria-label="$t('a11y.close_image_gallery')"
      :left-arrow-aria-label="$t('a11y.previous_image')"
      :right-arrow-aria-label="$t('a11y.next_image')"
      :error-image-handler="
        (src) =>
          coreStore.handleImageLoadError({
            src: src,
            source: 'activity-details-fullscreen-single-image-gallery',
          })
      "
      @close="closeFullScreenSingleImageGallery"
    />
  </div>
</template>

<style scoped lang="scss">
.activity-details-gallery {
  border-radius: var(--border-radius-12);
  overflow: hidden;
  position: relative;

  .masonry-gallery {
    width: 100%;
    aspect-ratio: 1/1;
    @include for-less-or-equal-than-1200-px {
      height: 425px;
      aspect-ratio: unset;
    }
    @include for-less-or-equal-than-900-px {
      height: 485px;
    }
    @include for-less-or-equal-than-599-px {
      height: 350px;
    }
  }
}
</style>
